import { html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Overlay, MenuItem } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { MarkdownLinkEvent, MarkdownEditor } from "@omegagrid/markdown";
import rp, { AppComponent, helpers } from "@reporting/rp-app-host";
import { style } from "./markdownContainer.style";
import { MessagesContainer, Sidebar, TicketContainer } from '../components';
import { AttachmentFile, FILE_TYPES, REQUEST_STATUSES, REQUEST_TYPES } from "../types";
import { File as TicketFile, Message } from '@reporting/rp-types/helpdesk';
import { Ticket } from '@reporting/rp-types/helpdesk';

// below this width (px) of the container the Status/Type dropdowns collapse into one menu (they would be squeezed to nothing)
const COMPACT_CONTROLS_WIDTH = 480;

@customElement('rp-helpdesk-markdown-container')
export class MarkdownContainer extends AppComponent {

	static styles = [style];

	@state()
	private _compactControls = false;

	private _resizeObserver = new ResizeObserver(() => {
		if (this.offsetWidth === 0) return; // hidden (inactive tab) - keep the last known layout
		this._compactControls = this.offsetWidth < COMPACT_CONTROLS_WIDTH;
	});

	connectedCallback() {
		super.connectedCallback();
		this._resizeObserver.observe(this);
	}

	disconnectedCallback() {
		this._resizeObserver.disconnect();
		super.disconnectedCallback();
	}

	@property({type: Object})
	ticket: Ticket;

	@property({type: Object})
	msgComponent: MessagesContainer;

	@property({type: Object})
	ticketComponent: TicketContainer;

	@query('og-overlay')
	loader: Overlay;

	@query('og-markdown-editor')
	markdown: MarkdownEditor;

	@query('#attachments')
	attachmentsElement: HTMLDivElement;

	msgAttachments: AttachmentFile[] = [];
	maxFileSize: number;

	@state()
	private _pendingStatus: number = null;

	@state()
	private _pendingType: number = null;

	get selectedStatus() { return this._pendingStatus ?? this.ticket?.status; }
	get selectedType() { return this._pendingType ?? this.ticket?.id_type; }

	async firstUpdated() {
		this.maxFileSize = await rp.ms('helpdesk').get<number>('files/maxSize');
	}

	private _listLabel(list: {k: number, v: string}[], key: number): string {
		const item = list.find(i => i.k === key);
		return item ? msg(item.v) : '—';
	}

	private _statusItems = (): MenuItem[] => REQUEST_STATUSES.map(item => ({
		key: item.k,
		value: msg(item.v),
		exec: () => { this._pendingStatus = item.k; this.ticketComponent.edit(); }
	}));

	private _typeItems = (): MenuItem[] => REQUEST_TYPES.map(item => ({
		key: item.k,
		value: msg(item.v),
		exec: () => { this._pendingType = item.k; this.ticketComponent.edit(); }
	}));

	// Compact layout: Status and Type share one menu, sections headed by a label, the current value ticked.
	private _compactItems = (): MenuItem[] => {
		const section = (label: string, items: MenuItem[], selected: number): MenuItem[] => [
			{key: `label-${label}`, value: label, type: 'label'},
			...items.map(item => ({...item, icon: item.key === selected ? 'check' : null})),
		];
		return [
			...(rp.userProvider.isSA() ? [
				...section(msg('Status'), this._statusItems(), this.selectedStatus),
				{key: 'divider', divider: true},
			] : []),
			...section(msg('Type'), this._typeItems(), this.selectedType),
		];
	};

	private _getTicketChanges(): Partial<Ticket> {
		const changes: Partial<Ticket> = {};
		if (this._pendingStatus !== null && this._pendingStatus !== this.ticket.status) changes.status = this._pendingStatus;
		if (this._pendingType !== null && this._pendingType !== this.ticket.id_type) changes.id_type = this._pendingType;
		return Object.keys(changes).length ? changes : null;
	}

	private async _applyTicketChanges(changes: Partial<Ticket>) {
		const accounting = this.ticket.accounting;
		await rp.ms('helpdesk').post<Ticket>('tickets', { id_request: this.ticket.id_request, ...changes }, { accounting });
		// system message in the same format as EditTicketFormAdapter.save
		let text = '';
		if (changes.status !== undefined) text += `${msg('Change')} *${msg('Status')}* → **${this._listLabel(REQUEST_STATUSES, changes.status)}**. `;
		if (changes.id_type !== undefined) text += `${msg('Change')} *${msg('Type')}* → **${this._listLabel(REQUEST_TYPES, changes.id_type)}**. `;
		const sysMessage: Message = {
			id_request: this.ticket.id_request,
			sys_message: true,
			text,
		};
		await rp.ms('helpdesk').post<Message>(`messages`, sysMessage, { accounting });
		Object.assign(this.ticket, changes);
		this._pendingStatus = null;
		this._pendingType = null;
		// sync map access: components.get() returns a Promise (and force-creates) when the sidebar is not registered
		(rp.host.components.components.get('helpdesk') as Sidebar)?.refresh();
	}

	async uploadFile(file: File, idMsg: number): Promise<TicketFile> {
		const base64Content = await helpers.fileToDataUrl(file);
	    const ticketFile: TicketFile = {
	        id_message: idMsg,
	        file_name: file.name,
	        origin_file_name: file.name,
	        content: base64Content.split(',')[1], // remove prefix "data:...;base64,"
	    };
	    return await rp.ms('helpdesk').post<TicketFile>(`files`, ticketFile, { accounting: this.ticket.accounting });
	}

	renderAttachment(file: File): string {
		const url = URL.createObjectURL(file);
		// attachment element
		const aFile = document.createElement('a');
		aFile.classList.add('attachment');
		aFile.href = url;
		aFile.innerText = file.name;
		aFile.target = '_blank';
		// remove button
		const aRemove = document.createElement('a');
		aRemove.href = 'javascript:;';
		aRemove.innerHTML = `<og-icon icon="xmark" style="color: red;"></og-icon>`;
		aRemove.onclick = () => this._removeAttachment(aFile);
		aFile.appendChild(aRemove);
		// append to attachments
		this.attachmentsElement.appendChild(aFile);
		return url;
	}

	_removeAttachment(a: HTMLAnchorElement) {
		this.attachmentsElement.removeChild(a);
		const index = this.msgAttachments.findIndex(msg => msg.url === a.href);
		if (index >= 0) this.msgAttachments.splice(index, 1);
	}

	_onImgUpload = async (file: File): Promise<string> => {
		// max file size validation
		if (file.size > this.maxFileSize) {
			alert(`File '${file.name}' exceeds the maximum limit of ${this.maxFileSize} bytes!`);
			return '';
		}
		const url = URL.createObjectURL(file);
		const img = new Image();
		img.src = url;
		img.onload = () => {
			this.msgAttachments.push(
				{file, url, markdownImage: {width: img.width, height: img.height}}
			);
		};
		return url;
	}

	_onChange = () => {
		this.ticketComponent.edit();
	}

	_onAttachment = () => {
		const input = document.createElement('input');
		input.type = 'file';
		input.multiple = true;
		input.style.display = 'none';
		// by extension, as validated below: the system may know no MIME type for some of them
		// (.msg without Outlook installed), and a MIME-only filter would hide such files in the dialog
		input.accept = Array.from(FILE_TYPES.keys()).map(ext => `.${ext}`).join(',');
		input.onchange = () => {
			for (const file of Array.from(input.files)) {
				// max file size validation
				if (file.size > this.maxFileSize) {
					alert(`File '${file.name}' exceeds the maximum limit of ${this.maxFileSize} bytes!`);
					continue;
				}
				// type validation
				if (!FILE_TYPES.has(file.name.split('.').pop().toLowerCase())) {
					alert(`File '${file.name}' has unsupported type!`);
					continue;
				}
				const url = this.renderAttachment(file);
				this.msgAttachments.push({file: file, url});
			}
		};
		input.click();
	}

	_onLink = (e: MarkdownLinkEvent) => {
		e.preventDefault();
		// custom link click logic
		const href = e.a.getAttribute('href');
		if (!href) return;
		if (href.startsWith('#')) {
			this.markdown.scrollToSelector(href);
		} else {
			window.open(href, '_blank');
		}
	}

	_onSend = async () => {
		let content = this.markdown.content;
		const changes = this._getTicketChanges();
		if (!content && !changes) {
			rp.host.bucket.showAlert({content: msg('Message cannot be empty!'), type: 'warning'});
			return;
		}
		this.loader.show(this);
		if (content) {
			let message: Message = {
				id_request: this.ticket.id_request,
				sys_message: false,
				text: '',
			};
			// save message to get id
			message = await rp.ms('helpdesk').post<Message>(`messages`, message, { accounting: this.ticket.accounting });
			// upload all attachments and replace url for markdown images
			for (const attachment of this.msgAttachments) {
				if (attachment.markdownImage && !content.includes(attachment.url.toString())) continue;
				const newFile = await this.uploadFile(attachment.file, message.id_message);
				if (attachment.markdownImage) {
					// images are saved like this: ![ratio](url)
					const imrUrl = attachment.url.toString();
					const imgRatio = parseFloat(content.substring(content.indexOf(imrUrl) - 6, content.indexOf(imrUrl) - 2));
					content = content.replace(
						imrUrl,
						`${newFile.idz}?width=${attachment.markdownImage.width * imgRatio}&height=${attachment.markdownImage.height * imgRatio}`
					);
				}
			}
			message.text = content;
			// update message with content
			await rp.ms('helpdesk').patch<Message>(`messages/${message.id_message}`, message, { accounting: this.ticket.accounting });
			this.msgAttachments = [];
			this.markdown.content = '';
			this.attachmentsElement.innerHTML = '';
		}
		// ticket changes go AFTER the message so their system message follows it
		if (changes) await this._applyTicketChanges(changes);
		// refresh messages
		this.msgComponent?.refresh();
		this.ticketComponent.save();
		this.loader.hide();
	}

	render = () => html`
		<og-overlay defaultLoader></og-overlay>
		<og-markdown-editor
			maxContentWidth="2000"
			.onUpload="${this._onImgUpload}"
			@change="${this._onChange}"
			@markdown.link="${this._onLink}">
		</og-markdown-editor>
		<div class="controls">
			<div class="controls-box">
				${this._compactControls ? html`
					<og-dropdown-menu
						class="ticket-prop ticket-prop-compact"
						openDirection="up"
						dynamicWidth
						noCaret
						alignment="left"
						color="transparent"
						icon="sliders"
						title="${msg('Status')} / ${msg('Type')}"
						.items="${this._compactItems()}">
					</og-dropdown-menu>
				` : html`
					${rp.userProvider.isSA() ? html`
						<og-dropdown-menu
							class="ticket-prop"
							openDirection="up"
							dynamicWidth
							alignment="left"
							color="transparent"
							.items="${this._statusItems()}">
							<div slot="label">${msg('Status')}: <b>${this._listLabel(REQUEST_STATUSES, this.selectedStatus)}</b></div>
						</og-dropdown-menu>
					` : ''}
					<og-dropdown-menu
						class="ticket-prop"
						openDirection="up"
						dynamicWidth
						alignment="left"
						color="transparent"
						.items="${this._typeItems()}">
						<div slot="label">${msg('Type')}: <b>${this._listLabel(REQUEST_TYPES, this.selectedType)}</b></div>
					</og-dropdown-menu>
				`}
				<div style="flex: 1"></div>
				<div id="attachments"></div>
				<div
					class="btn-attach"
					title="${msg('Attach file')}"
					@click="${() => this._onAttachment()}">
					<og-icon icon="paperclip"></og-icon>
				</div>
				<div
					class="btn-send"
					title="${msg('Send message')}"
					@click="${() => this._onSend()}">
					<og-icon icon="send"></og-icon>
				</div>
			</div>
		</div>
	`;

}
